import { useEffect, useRef, useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Settings, ListChecks, ScrollText, Accessibility, LogOut, DatabaseZap, Rocket } from "lucide-react";
import { api, clearAdminToken, getAdminToken, refreshAdminSession } from "@/lib/api";
import { AppIcon } from "@/components/Brand";
import { ADMIN } from "@/constants/testIds";

export default function AdminLayout() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const lastRefreshRef = useRef(0);

  useEffect(() => {
    if (!getAdminToken()) { navigate("/admin/login"); return; }
    api.get("/admin/me").then((r) => setMe(r.data)).catch(() => {
      clearAdminToken(); navigate("/admin/login");
    });
  }, [navigate]);

  // Rolling 60-min inactivity window. On any user activity, refresh the token
  // at most once every 5 minutes. If the user is idle, the token expires on
  // its own and the api response interceptor redirects to login.
  useEffect(() => {
    const REFRESH_MS = 5 * 60 * 1000;
    const onActivity = () => {
      if (!getAdminToken()) return;
      const now = Date.now();
      if (now - lastRefreshRef.current < REFRESH_MS) return;
      lastRefreshRef.current = now;
      refreshAdminSession().catch(() => { /* interceptor handles 401 */ });
    };
    const events = ["click", "keydown", "scroll", "mousemove"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, onActivity));
  }, []);

  const logout = async () => {
    try { await api.post("/admin/logout"); } catch (e) { /* ignore */ }
    clearAdminToken();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen flex" style={{ background: "#F4F8FB" }}>
      <aside className="w-64 border-r flex-none hidden lg:block" style={{ borderColor: "#E2E8F0", background: "#fff" }}>
        <div className="p-5 border-b" style={{ borderColor: "#E2E8F0" }}>
          <Link to="/" className="flex items-center gap-3">
            <AppIcon size={40} />
            <div>
              <div className="font-serif text-base" style={{ color: "#0B2D59" }}>Pathways to Purpose</div>
              <div className="text-xs" style={{ color: "#475569" }}>Admin dashboard</div>
            </div>
          </Link>
        </div>
        <nav className="p-3 space-y-1">
          <NavItem to="/admin" icon={LayoutDashboard} label="Applications" testId={ADMIN.navDashboard} end />
          <NavItem to="/admin/settings" icon={Settings} label="Property settings" testId={ADMIN.navSettings} />
          <NavItem to="/admin/criteria" icon={ListChecks} label="Objective criteria" testId={ADMIN.navCriteria} />
          <NavItem to="/admin/accommodations" icon={Accessibility} label="Accommodation inbox" testId={ADMIN.navAccommodation} />
          <NavItem to="/admin/retention" icon={DatabaseZap} label="Data retention" testId="admin-nav-retention" />
          <NavItem to="/admin/launch" icon={Rocket} label="Launch checklist" testId="admin-nav-launch" />
          <NavItem to="/admin/audit" icon={ScrollText} label="Audit log" testId={ADMIN.navAudit} />
        </nav>
        <div className="absolute bottom-0 w-64 p-3 border-t" style={{ borderColor: "#E2E8F0", background: "#fff" }}>
          <div className="text-xs mb-2" style={{ color: "#475569" }}>{me?.email}</div>
          <button onClick={logout} className="p2p-btn-outline w-full inline-flex items-center justify-center gap-2"
            style={{ padding: "0.5rem 1rem" }} data-testid={ADMIN.logoutBtn}>
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <div className="lg:hidden border-b sticky top-0 z-20 flex items-center justify-between px-4 py-3"
          style={{ background: "#fff", borderColor: "#E2E8F0" }}>
          <Link to="/admin" className="flex items-center gap-2" style={{ color: "#0B2D59" }}>
            <AppIcon size={32} />
            <span className="font-serif text-lg">Pathways Admin</span>
          </Link>
          <button onClick={logout} className="text-sm underline" style={{ color: "#475569" }}>Sign out</button>
        </div>
        <div className="lg:hidden border-b flex overflow-x-auto px-2 py-2 gap-2" style={{ background: "#fff", borderColor: "#E2E8F0" }}>
          <MobileLink to="/admin" label="Applications" end />
          <MobileLink to="/admin/settings" label="Settings" />
          <MobileLink to="/admin/criteria" label="Criteria" />
          <MobileLink to="/admin/accommodations" label="Accommodations" />
          <MobileLink to="/admin/retention" label="Retention" />
          <MobileLink to="/admin/launch" label="Launch" />
          <MobileLink to="/admin/audit" label="Audit" />
        </div>
        <Outlet />
      </div>
    </div>
  );
}

function NavItem({ to, icon: Icon, label, testId, end }) {
  return (
    <NavLink to={to} end={end} data-testid={testId}
      className={({ isActive }) =>
        "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition " +
        (isActive ? "font-semibold" : "")}
      style={({ isActive }) => ({
        background: isActive ? "#E2EBF1" : "transparent",
        color: isActive ? "#0B2D59" : "#0B2D59",
      })}>
      <Icon className="w-4 h-4" /> {label}
    </NavLink>
  );
}

function MobileLink({ to, label, end }) {
  return (
    <NavLink to={to} end={end}
      className="px-3 py-1.5 rounded-full text-sm flex-none"
      style={({ isActive }) => ({
        background: isActive ? "#0B2D59" : "#E2EBF1",
        color: isActive ? "#F4F8FB" : "#0B2D59",
      })}>{label}</NavLink>
  );
}
